import { useQuery, useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useCallback, useMemo, useRef } from "react";
import { sharedMutators } from "../../../../shared/mutators";
import { MAX_USER_GROUP_NAME_LENGTH } from "../../../../shared/mutators/createUserGroup";
import { queries } from "../../../../shared/queries";
import { isTenantScopeGrant } from "../../../../shared/rbac/permissions";
import { GROUP_DELETE_BLOCKED_MESSAGE } from "../../../../shared/rbac/validation/groupDeletable";
import {
  type GrantEntry,
  grantHasPermission,
  type RoleOption,
  rolesForScope,
} from "../../../../shared/rbac/roles";
import { customRolesForScope } from "../../../lib/grantAssembly";
import { defaultRoleForScope } from "../../../lib/defaultRoleForScope";
import { useGroupRemovalDisconnectGuard } from "../../../hooks/useGroupRemovalDisconnectGuard";
import { useHasAccess } from "../../../hooks/useHasAccess";
import { useManageAccessGuard } from "../../../hooks/useManageAccessGuard";
import { useCurrentUserId } from "../../../hooks/useCurrentUserId";
import { useNameField } from "../../../hooks/useNameField";
import { useIdpSyncLock } from "../../../hooks/useIdpSyncLock";
import { useTenantRoles } from "../../../hooks/useTenantRoles";
import { usePrincipalModals } from "../../../hooks/usePrincipalModals";
import { useResolveScopeName } from "../../../hooks/useResolveScopeName";
import { useMakeUrl, navigate, routes, useCurrentUrl } from "../../../lib/urls";
import { AccessMapPanel } from "../../AccessMapPanel";
import { Box } from "../../Box";
import { Button } from "../../Button";
import { Input } from "../../Input";
import { useScopeAccessCloseWarning } from "../../ScopeAccessCloseWarning";
import { ScopeAccessTable } from "../../ScopeAccessTable";
import { TabbedModal } from "../../TabbedModal";
import { TenantRolesChecklist } from "../../TenantRolesChecklist";
import { Text } from "../../Text";
import { Card, ColoredIconBadge, FieldLabel, SectionHeading } from "../shared";
import { DangerSection } from "../DangerSection";
import { useOpenResource } from "../lib/useOpenResource";
import { PrincipalAccessTable } from "../PrincipalAccessTable";
import { ScopeAccessEditor } from "../ScopeAccessEditor";
import { inferScopeKind, isEditableScopeKind } from "../lib/inferScopeKind";

type Props = {
  groupId: string;
  onClose: () => void;
};

type Tab = "settings" | "access";
const TABS = [
  { key: "settings", label: "Settings", icon: "settings-2" },
  { key: "access", label: "Access", icon: "lock" },
] as const;

const TabPanel = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export function UserGroupSettings({ groupId, onClose }: Props) {
  const makeUrl = useMakeUrl();
  const z = useZero();
  // Deleting a group is a lifecycle action JIT never performs, so it's available
  // under both JIT modes and blocked only under SCIM. Editing members — and
  // renaming, since the name is the IdP's group-matching key — is owned by the
  // IdP whenever groups are synced, so both are locked under "jit_users_and_groups"
  // and SCIM but stay editable under plain "jit".
  const { locked: groupLifecycleLocked, reason: groupLifecycleReason } =
    useIdpSyncLock("groupLifecycle");
  const { locked: membershipLocked, reason: membershipReason } =
    useIdpSyncLock("groupMembership");
  const currentUserId = useCurrentUserId();
  const canManage = useHasAccess("user_group:write", groupId);
  const canAssignTenantRoles = useHasAccess("tenant:roles_manage");
  const canReadSpaces = useHasAccess("tenant:spaces_read");
  const canReadApps = useHasAccess("tenant:connected_apps_read");
  const canReadSkills = useHasAccess("tenant:skills_read");
  const [allGroups] = useQuery(queries.allUserGroupsLite());
  const [groupGrants] = useQuery(
    queries.roleGrantsByScope({ scopeId: groupId })
  );
  const [principalGrants] = useQuery(
    queries.roleGrantsByPrincipalGroup({ userGroupId: groupId })
  );
  const [allUsers] = useQuery(queries.allUsers());
  const [customRoles] = useQuery(queries.allRolesLite());
  const [allConnectors] = useQuery(queries.connectedAppsLite());
  const { resolveScopeName, personalSpaceIds } = useResolveScopeName();
  const { onOpenUser } = usePrincipalModals();
  const inputRef = useRef<HTMLInputElement>(null);
  const { stagedGrantsRef, requestClose, closeWarningModal } =
    useScopeAccessCloseWarning({ singular: "member", plural: "members" });
  const {
    stagedGrantsRef: accessStagedRef,
    requestClose: requestCloseAccess,
    closeWarningModal: accessCloseWarning,
  } = useScopeAccessCloseWarning({ singular: "resource", plural: "resources" });
  const { tab } = useCurrentUrl(routes.modal.group, { id: groupId });
  const activeTab = tab as Tab;

  const groupCustomRoles = useMemo(
    () => customRolesForScope(customRoles, "user_group"),
    [customRoles]
  );

  const roleOptions: RoleOption[] = useMemo(
    () => rolesForScope("user_group", groupCustomRoles),
    [groupCustomRoles]
  );

  const group = useMemo(
    () => allGroups.find((g: { id: string }) => g.id === groupId),
    [allGroups, groupId]
  );

  const nameField = useNameField({
    currentName: group?.name,
    allItems: allGroups,
    currentId: groupId,
    onCommit: useCallback(
      (name: string) => {
        z.mutate(
          sharedMutators.updateUserGroup({ userGroupId: groupId, name })
        );
      },
      [z, groupId]
    ),
  });

  const activeUsers = useMemo(
    () => (allUsers ?? []).filter((u) => !u.deactivated),
    [allUsers]
  );

  const memberUserIds = useMemo(
    () => new Set(groupGrants.map((g) => g.userId).filter(Boolean) as string[]),
    [groupGrants]
  );

  const allGrants: GrantEntry[] = useMemo(
    () =>
      groupGrants
        .filter((g): g is typeof g & { userId: string } => !!g.userId)
        .map((g) => ({
          userId: g.userId,
          builtinRoleId: g.builtinRoleId ?? undefined,
          customRoleId: g.customRoleId ?? undefined,
        })),
    [groupGrants]
  );

  const assignedUsers = useMemo(
    () =>
      (allUsers ?? []).filter((u) => memberUserIds.has(u.id) && !u.deactivated),
    [allUsers, memberUserIds]
  );

  const tenantRoleCommit = useCallback(
    (builtinRoleIds: string[], customRoleIds: string[]) => {
      z.mutate(
        sharedMutators.setUserGroupTenantRoles({
          userGroupId: groupId,
          builtinRoleIds,
          customRoleIds,
        })
      );
    },
    [z, groupId]
  );

  const {
    allTenantRoles,
    assignedBuiltinRoleIds,
    assignedCustomRoleIds,
    toggleTenantRole,
  } = useTenantRoles({
    grants: principalGrants,
    onCommit: tenantRoleCommit,
  });

  const rolesMap = useMemo(
    () =>
      new Map(
        customRoles.map((r) => [
          r.id,
          { id: r.id, name: r.name, scopeKind: r.scopeKind },
        ])
      ),
    [customRoles]
  );

  const appsById = useMemo(
    () => new Map(allConnectors.map((a) => [a.id, a])),
    [allConnectors]
  );

  const accessGrants = useMemo(
    () =>
      principalGrants.filter(
        (g) => !isTenantScopeGrant(g) && !personalSpaceIds.has(g.scopeId)
      ),
    [principalGrants, personalSpaceIds]
  );

  const otherAccessGrants = accessGrants.filter(
    (grant) => !isEditableScopeKind(inferScopeKind(grant, rolesMap))
  );

  const hasScopeGrants = useMemo(
    () => principalGrants.some((g) => g.scopeId !== groupId),
    [principalGrants, groupId]
  );

  const { openResource } = useOpenResource();

  const customRoleMap = useMemo(
    () => new Map(groupCustomRoles.map((r) => [r.id, r])),
    [groupCustomRoles]
  );

  // A group's "manager" is any member whose role grants user_group:write.
  const isManagerGrant = useCallback(
    (g: GrantEntry) =>
      grantHasPermission(
        {
          builtinRoleId: g.builtinRoleId ?? null,
          customRole: g.customRoleId
            ? (customRoleMap.get(g.customRoleId) ?? null)
            : null,
        },
        "user_group:write"
      ),
    [customRoleMap]
  );

  const viewerIsTenantAdmin = useHasAccess("allow_all");

  const viewerRetainsManage = useCallback(
    (grants: GrantEntry[]) =>
      viewerIsTenantAdmin ||
      grants.some((g) => isManagerGrant(g) && g.userId === currentUserId),
    [currentUserId, viewerIsTenantAdmin, isManagerGrant]
  );

  const { guard: guardRemoval, modal: disconnectModal } =
    useGroupRemovalDisconnectGuard({
      userGroupIds: useMemo(() => [groupId], [groupId]),
    });

  const doSetGroupGrants = useCallback(
    (grants: GrantEntry[]) => {
      const nextUserIds = new Set(
        grants.map((g) => g.userId).filter((id): id is string => id != null)
      );
      const removedUserIds = [...memberUserIds].filter(
        (id) => !nextUserIds.has(id)
      );
      guardRemoval(groupId, removedUserIds, () => {
        z.mutate(
          sharedMutators.setGroupGrants({
            userGroupId: groupId,
            grants: grants
              .filter((g): g is typeof g & { userId: string } => !!g.userId)
              .map((g) => ({
                userId: g.userId,
                builtinRoleId: g.builtinRoleId,
                customRoleId: g.customRoleId,
              })),
          })
        );
      });
    },
    [z, groupId, memberUserIds, guardRemoval]
  );

  const { commitGrants, confirmModal: manageAccessModal } =
    useManageAccessGuard({
      allGrants,
      doCommit: doSetGroupGrants,
      isManagerGrant,
      viewerRetainsManage,
      resourceLabel: "group",
    });

  const removeUser = useCallback(
    (userId: string) => {
      commitGrants(allGrants.filter((g) => g.userId !== userId));
    },
    [allGrants, commitGrants]
  );

  const handleDelete = useCallback(() => {
    z.mutate(sharedMutators.deleteUserGroup({ userGroupId: groupId }));
    onClose();
  }, [z, groupId, onClose]);

  const deleteDisabledReason = !canManage
    ? "You don’t have permission to delete this group"
    : groupLifecycleLocked
      ? (groupLifecycleReason ?? "")
      : hasScopeGrants
        ? GROUP_DELETE_BLOCKED_MESSAGE
        : null;

  if (!group) return null;

  const guardClose = (proceed: () => void) =>
    requestClose(() => requestCloseAccess(proceed));
  const close = () => guardClose(onClose);

  return (
    <>
      <TabbedModal
        icon={<ColoredIconBadge id={group.id} iconName="users-round" />}
        title={group.name}
        tabs={TABS.map((t) => ({
          ...t,
          to: makeUrl(routes.modal.group, { id: groupId, tab: t.key }),
        }))}
        activeTab={activeTab}
        onTabChange={(next) =>
          navigate(routes.modal.group, { id: groupId, tab: next })
        }
        onClose={close}
        footerRight={
          <Button size="l" onClick={close}>
            Done
          </Button>
        }
      >
        <TabPanel
          style={{ display: activeTab === "settings" ? "flex" : "none" }}
        >
          <Card>
            <FieldLabel>
              <Box horizontal={8} bottom={10}>
                <Text variant="text-small-md">Group name</Text>
              </Box>
              <Input
                ref={inputRef}
                value={nameField.value}
                onChange={(e) => nameField.setValue(e.target.value)}
                onBlur={nameField.commit}
                onKeyDown={(e) => {
                  if (e.key === "Enter") inputRef.current?.blur();
                }}
                maxLength={MAX_USER_GROUP_NAME_LENGTH}
                disabled={!canManage || membershipLocked}
                title={
                  membershipLocked ? (membershipReason ?? undefined) : undefined
                }
                style={{ width: "100%" }}
              />
              {nameField.nameTaken && (
                <Box horizontal={8} top={6}>
                  <Text variant="text-small" color="var(--text-danger)">
                    A group with this name already exists.
                  </Text>
                </Box>
              )}
            </FieldLabel>
          </Card>

          {canAssignTenantRoles && allTenantRoles.length > 0 && (
            <Card>
              <SectionHeading>
                <Text variant="text-regular-md">Tenant roles</Text>
                <Text variant="text-small" color="var(--text-secondary)">
                  Tenant-wide capabilities for everyone in this group.
                </Text>
              </SectionHeading>
              <TenantRolesChecklist
                roles={allTenantRoles}
                assignedBuiltinRoleIds={assignedBuiltinRoleIds}
                assignedCustomRoleIds={assignedCustomRoleIds}
                onToggle={toggleTenantRole}
                disabled={!canManage}
              />
            </Card>
          )}

          <DangerSection
            title="Delete group"
            description="Permanently deletes the group and removes all its members. Members keep their other access."
            actionLabel="Delete group"
            disabled={deleteDisabledReason != null}
            disabledReason={deleteDisabledReason ?? undefined}
            confirm={{ confirmLabel: "Delete", onConfirm: handleDelete }}
          />
        </TabPanel>

        <TabPanel style={{ display: activeTab === "access" ? "flex" : "none" }}>
          <AccessMapPanel nodeId={group.id} noun="group" guard={guardClose} />
          <Card>
            <SectionHeading>
              <Text variant="text-regular-md">
                Members ({assignedUsers.length})
              </Text>
              <Text variant="text-small" color="var(--text-secondary)">
                Add members and choose their role.
              </Text>
            </SectionHeading>
            <ScopeAccessTable
              allGrants={allGrants}
              assignedGroups={[]}
              assignedUsers={assignedUsers}
              roleOptions={roleOptions}
              allGroups={[]}
              allUsers={activeUsers}
              customRoles={groupCustomRoles}
              canManage={canManage}
              compact
              commitGrants={commitGrants}
              removeGroup={() => {}}
              removeUser={removeUser}
              memberRoster
              onOpenUser={onOpenUser}
              idpSyncLocked={membershipLocked}
              idpSyncReason={membershipReason}
              stagedGrantsRef={stagedGrantsRef}
              defaultRole={defaultRoleForScope("user_group", roleOptions)}
            />
          </Card>

          <Card>
            <SectionHeading>
              <Text variant="text-regular-md">Access</Text>
              <Text variant="text-small" color="var(--text-secondary)">
                Spaces, connectors, and skills this group can access.
              </Text>
            </SectionHeading>
            <ScopeAccessEditor
              active={activeTab === "access"}
              principal={{ userGroupId: groupId }}
              directGrants={accessGrants}
              customRoles={customRoles}
              resolveScopeName={resolveScopeName}
              canManage={canManage}
              stagedGrantsRef={accessStagedRef}
              onOpenResource={openResource}
              hideEmpty={otherAccessGrants.length > 0}
            />
            {otherAccessGrants.length > 0 && (
              <PrincipalAccessTable
                grants={otherAccessGrants}
                rolesMap={rolesMap}
                resolveScopeName={resolveScopeName}
                apps={appsById}
                emptyDescription="Grant access from a space’s, connector’s, or skill’s settings."
                onOpenResource={openResource}
                hasTenantRead={{
                  space: canReadSpaces,
                  connected_app: canReadApps,
                  skill: canReadSkills,
                }}
              />
            )}
          </Card>
        </TabPanel>
      </TabbedModal>
      {manageAccessModal}
      {disconnectModal}
      {closeWarningModal}
      {accessCloseWarning}
    </>
  );
}
